import axios, { AxiosResponse } from "axios"
import { formatDate } from "../services/dateService"
import { getToken } from "./authApi"
import { ApiResponse, BaseCreateResponse, BaseInfo, BaseResponse, BaseResponseWithData, baseUrl, deleteWithAuth, EmptyRequest, GetParams, getWithAuth, getWithAuthAndParams, Guid, ListRespone, PaginationListRespone, patchWithAuth, postWithAuth, postWithAuthWitoutParams } from "./baseApi"
import { ReorderItemPair, TimeSlotModel } from "./channelsApi"
import { MediaType } from "./mediaApi"

export type PlayNowList = {
    id: string
    name : string
    channels: BaseInfo[],
    status: PlayNowListStatus,
    includeTagIds: BaseInfo[],
    excludeTagIds: BaseInfo[],
    useORRelationshipForIncludeTags: boolean,
    players: {id:Guid, name: string, playerId:Guid, isLocked: boolean}[],
    autoDeactivationTime: string
}

export type PlayNowLog = {
    id:Guid,
    playNowLogType: PlayNowLogTypeEnum,
    dateTime: string,
    playNowListName: string,
    playerName: string,
    actionDescription: string,
    userName: string
}

export enum PlayNowLogTypeEnum {
    Activated,
    Deactivated,
    Locked,
    Unlocked
}


export type CreatePlayNowListRequest = {
    name : string,
    channelIds: string[],
    includeTagIds: Guid[],
    excludeTagIds: Guid[],
    useORRelationshipForIncludeTags: boolean,
    autoDeactivationTime: string
}

export type UpdatePlayNowListRequest = {
    name : string,
    channelIds: string[],
    includeTagIds: Guid[],
    excludeTagIds: Guid[],
    useORRelationshipForIncludeTags: boolean,
    autoDeactivationTime: string
}

export type PlayNowListMedia = {
    id: Guid,
    name: string,
    url: string,
    order: number,
    durationMs: number,
    type: MediaType,
    isTriggeringSkin: boolean,
    schedule: PlayNowMediaSchedule[],
    taxonomiesCategoryName: string
}

export type PlayNowListAddMediaRequest = {
    mediaIds: Guid[]
}

export type PlayNowListReorderMedia = {
    reorderItems: ReorderItemPair[]
}

export enum PlayNowListStatus {
    Active = 0,
    Inactive = 1
}

export interface PlayNowMediaSchedule {
    id: Guid,
    name: string,
    start: string,
    end: string,
    startTime: string,
    endTime: string
}

export type PlayNowMediaMediaScheduleRequest = Omit<PlayNowMediaSchedule, 'id'>

export type GroupPlayNowMedia = {
    id: Guid,
    name: string,
    type: number,
    singleMedia: PlayNowListMedia | null,
    groupMedia: PlayNowListMedia[] | null,
}

export type GroupForPlayNowMediaRequest = {
    groupId: Guid | null
}

export type GroupForPlayNowRequest = {
    groupId: Guid | null
}

export type ReorderedPlayNowIds = {
    ids: Guid[]
}

export type GroupedPlayNow = {
    groupId: Guid,
    name: string,
    playNowLists: PlayNowList[]
}

export type PlayNowLogsCSVRequest = {
  "playerId": Guid | null,
  "playlistId": Guid | null,
  "userId": Guid | null,
  "startDate": string | null,
  "endDate": string | null
}


export const getPlayNowLists = async() : Promise<ApiResponse<ListRespone<PlayNowList>>> => {
    return await getWithAuth<ListRespone<PlayNowList>>({url : `${baseUrl}play-now-list`});
}

export const getPlayNowList = async(id: Guid) : Promise<ApiResponse<BaseResponseWithData<PlayNowList>>> => {
    return await getWithAuth<BaseResponseWithData<PlayNowList>>({url : `${baseUrl}play-now-list/${id}`});
}

export const createPlayNowList = async(request : CreatePlayNowListRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CreatePlayNowListRequest,BaseCreateResponse>(`${baseUrl}play-now-list`, request);
}

export const updatePlayNowList = async(request : UpdatePlayNowListRequest, id: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<UpdatePlayNowListRequest,BaseResponse>(`${baseUrl}play-now-list/${id}`, request);
}

export const getPlayNowListMedias = async (id: Guid) : Promise<ApiResponse<ListRespone<PlayNowListMedia>>> => {
    return await getWithAuth<ListRespone<PlayNowListMedia>>({url: `${baseUrl}play-now-list/${id}/media`}); 
}

export const addMediaToPlayNowList = async (id: Guid, request: PlayNowListAddMediaRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<PlayNowListAddMediaRequest, BaseResponse>(`${baseUrl}play-now-list/${id}/media`, request); 
}

export const getPlayNowMediaSchedule = async (listId: Guid, id: Guid) : Promise<ApiResponse<ListRespone<PlayNowMediaSchedule>>> => {
    return await getWithAuth<ListRespone<PlayNowMediaSchedule>>({url: `${baseUrl}play-now-list/${listId}/media/${id}/schedule`}); 
}

export const addPlayNowMediaSchedule = async (request: PlayNowMediaMediaScheduleRequest, listId : Guid, mediaId:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<PlayNowMediaMediaScheduleRequest,BaseCreateResponse>(`${baseUrl}play-now-list/${listId}/media/${mediaId}/schedule`, request);
}

export const deletePlayNowMediaSchedule = async (listId : Guid, mediaId:Guid, periodId: Guid): Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}play-now-list/${listId}/media/${mediaId}/schedule`, periodId);
}

// export const getPlayNowListMedia = async (id: Guid) : Promise<ApiResponse<ListRespone<PlayNowListMedia>>> => {
//     return await getWithAuth<ListRespone<PlayNowListMedia>>({url : `${baseUrl}play-now-list/${id}/media`});
// }

export const reorderPlayNowListMedia = async (id: Guid, request: PlayNowListReorderMedia) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<PlayNowListReorderMedia, BaseResponse>(`${baseUrl}play-now-list/${id}/media/reorder`, request); 
}

export const playNowListDeleteMedia = async (id: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}play-now-list/${id}/media`, mediaId); 
}

export const enablePlayNowList = async(id: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuthWitoutParams<BaseResponse>(`${baseUrl}play-now-list/${id}/play`);
}

export const disablePlayNowList = async(id: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuthWitoutParams<BaseResponse>(`${baseUrl}play-now-list/${id}/stop`);
}

export const deletePlayNowListApi = async (id: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}play-now-list`, id)
}

export const enableSkinPlayNowMedia = async (id: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}play-now-list/${id}/media/${mediaId}/enable-skin`, {}); 
}

export const disableSkinPlayNowMedia = async (id: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}play-now-list/${id}/media/${mediaId}/disable-skin`, {}); 
}

export const getPlayNowLogs = async (playerId: Guid, playlistId: Guid, userId: Guid, startDate:string, endDate:string, page: number, pageSize: number) : Promise<ApiResponse<PaginationListRespone<PlayNowLog>>> => {
    var params = new GetParams();
    params.Add("playerId", playerId);
    params.Add("playlistId", playlistId);
    params.Add("userId", userId);
    params.Add("startDate", startDate);
    params.Add("endDate", endDate);
    params.Add("page", page+"");
    params.Add("pageSize", pageSize+"");
    return await getWithAuthAndParams<PaginationListRespone<PlayNowLog>>(`${baseUrl}play-now-list/logs`, params); 
}

export const lockPlayer = async(playNowListId: Guid, playerId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<EmptyRequest, BaseCreateResponse>(`${baseUrl}play-now-list/${playNowListId}/player/${playerId}/lock`, {});
}
export const unlockPlayer = async(playNowListId: Guid, playerId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<EmptyRequest, BaseCreateResponse>(`${baseUrl}play-now-list/${playNowListId}/player/${playerId}/unlock`, {});
}


// groups
export const getGroupedPlayNowMedia = async (playNowListId: Guid) : Promise<ApiResponse<ListRespone<GroupPlayNowMedia>>> =>{
    return await getWithAuth<ListRespone<GroupPlayNowMedia>>({url: `${baseUrl}play-now-list/${playNowListId}/media/grouped`})
}

export const updateGroupForPlayNowMedia = async (request: GroupForPlayNowMediaRequest, playNowListId: Guid, mediaId:Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<GroupForPlayNowMediaRequest, BaseCreateResponse>(`${baseUrl}play-now-list/${playNowListId}/media/${mediaId}`, request); 
}

export const getGroupsForPlayNowMedia = async (playNowListId: Guid) : Promise<ApiResponse<ListRespone<BaseInfo>>> =>{
    return await getWithAuth<ListRespone<BaseInfo>>({url: `${baseUrl}play-now-list/${playNowListId}/groups`})
}

export const createGroupForPlayNowMedia = async (request: {name:string}, playNowListId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<{name:string}, BaseResponse>(`${baseUrl}play-now-list/${playNowListId}/groups`, request); 
}

export const changeGroupNameForPlayNowMedia = async (request: {name:string}, groupId: Guid, playNowListId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<{name:string}, BaseResponse>(`${baseUrl}play-now-list/${playNowListId}/groups/${groupId}`, request); 
}

export const cloneGroupPlayNowMedia = async (playNowListId: Guid, groupId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}play-now-list/${playNowListId}/groups/${groupId}/clone`, {}); 
}

export const removeGroupPlayNowMedia = async (playNowListId: Guid, groupId: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}play-now-list/${playNowListId}/groups`, groupId)
}

export const cloneGroupedPlayNowMedia = async (playNowListId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}play-now-list/${playNowListId}/media/grouped/${mediaId}/clone`, {}); 
}

export const reorderGroupPlayNowMedia = async (request: ReorderedPlayNowIds, playNowListId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ReorderedPlayNowIds, BaseResponse>(`${baseUrl}play-now-list/${playNowListId}/media/grouped/reorder`, request); 
}

export const getGroupedPlayNowList = async() : Promise<ApiResponse<ListRespone<GroupedPlayNow>>> => {
    return await getWithAuth<ListRespone<GroupedPlayNow>>({url : `${baseUrl}play-now-list/grouped`});
}

export const getGroupsForPlayNow = async () : Promise<ApiResponse<ListRespone<BaseInfo>>> =>{
    return await getWithAuth<ListRespone<BaseInfo>>({url: `${baseUrl}play-now-list/groups`})
}

export const createGroupForPlayNow = async (request: {name:string}) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<{name:string}, BaseResponse>(`${baseUrl}play-now-list/groups`, request); 
}

export const changeGroupNameForPlayNow = async (request: {name:string}, groupId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<{name:string}, BaseResponse>(`${baseUrl}play-now-list/groups/${groupId}`, request); 
}

export const updateGroupForPlayNow = async (request: GroupForPlayNowRequest, playNowListId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<GroupForPlayNowRequest, BaseCreateResponse>(`${baseUrl}play-now-list/${playNowListId}/group`, request); 
}

export const cloneGroupPlayNow = async (groupId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}play-now-list/groups/${groupId}/clone`, {}); 
}

export const removeGroupForPlayNow = async (groupId: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}play-now-list/groups`, groupId)
}

export const downloadCSVPlayNowLogsData = async (request: PlayNowLogsCSVRequest) : Promise<{data: any, filename: string}> => {
    const response: AxiosResponse<Blob> = await axios.post(`${baseUrl}play-now-list/logs/csv`, request, {
        headers: {
          'Authorization' : getToken()
        },
        responseType: 'blob',
    });

    const fileName =  `playnow_logs_${formatDate(new Date().toISOString())}.csv`;

    return {
        data: response.data,
        filename: fileName
    }
}

export const shufflePlayNowMediaItems = async (groupId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}play-now-list/groups/${groupId}/shuffle`, {}); 
}
